import { Button, Flex, Typography } from "antd";
import { ExclamationCircleOutlined, SyncOutlined } from "@ant-design/icons";
import { ReactNode } from "react";
import "../PageState/pageState.scss";
import { ErrorBoundary, FallbackProps } from "react-error-boundary";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";

/**
 * Phase 6 06-02 D-11 — chunk-load vs runtime error branching.
 *
 * ChunkLoadError is what react-error-boundary sees when a user visits the app
 * after a deploy — the SPA's lazy-loaded route chunk 404s because the hashed
 * filename has changed. For that case, "A new update is available. Please
 * refresh" is the honest message.
 *
 * Any other error is a real runtime bug. We don't lie to the user with "please
 * refresh" — instead we show an honest "this page hit an unexpected error"
 * copy with two actions: Reload and Return to streams.
 *
 * The copy is in common.json (`errorBoundary.*`). i18n runs with `useSuspense: false`
 * (src/i18n/index.ts), so a fallback never suspends while its strings load.
 *
 * IMPORTANT: uses the `FallbackComponent` prop, NOT the legacy `fallback={...}`
 * form. react-error-boundary v4 (^4.0.13 in package.json) makes `fallback`,
 * `FallbackComponent`, and `fallbackRender` mutually exclusive — the
 * `fallback: ReactElement` form cannot access the error object because it
 * renders eagerly. FallbackComponent receives FallbackProps with the current
 * error at crash time, which is what we need to branch on.
 */

/** A new deploy replaced the route chunk: an update notice, not an error, so the neutral tile. */
export function ChunkLoadFallback() {
  const { t } = useTranslation("common");
  return (
    <div className="st-page-state">
      <span className="st-page-state__tile st-page-state__tile--neutral" aria-hidden="true">
        <SyncOutlined />
      </span>
      <Typography.Paragraph className="st-page-state__text st-page-state__text--lead">
        {t("errorBoundary.updateAvailable")}
      </Typography.Paragraph>
      <Button type="primary" onClick={() => window.location.reload()}>
        {t("errorBoundary.refresh")}
      </Button>
    </div>
  );
}

/** A real crash: SeriousAlert's red tile, a Rubik title and an inverse recovery button. */
export function RuntimeErrorFallback({ onReset }: { onReset: () => void }) {
  const { t } = useTranslation("common");
  const navigate = useNavigate();
  return (
    <div className="st-page-state" role="alert">
      <span className="st-page-state__tile st-page-state__tile--error" aria-hidden="true">
        <ExclamationCircleOutlined />
      </span>
      <Typography.Title level={4} className="st-page-state__title">
        {t("errorBoundary.crashed")}
      </Typography.Title>
      <Flex gap="small" wrap="wrap" justify="center">
        <Button className="st-btn-inverse" onClick={() => window.location.reload()}>
          {t("errorBoundary.reload")}
        </Button>
        <Button
          onClick={() => {
            navigate("/dashboard/live-streams");
            onReset();
          }}
        >
          {t("errorBoundary.returnToStreams")}
        </Button>
      </Flex>
    </div>
  );
}

const CHUNK_LOAD_PATTERNS = [
  /Loading chunk \d+ failed/i,
  /Failed to fetch dynamically imported module/i,
  /error loading dynamically imported module/i,
  /Importing a module script failed/i,
];

function RefreshErrorBoundaryFallback({ error, resetErrorBoundary }: FallbackProps) {
  const err = error as Error | undefined;
  const message = err?.message ?? "";
  const isChunkLoad =
    err?.name === "ChunkLoadError" ||
    CHUNK_LOAD_PATTERNS.some((pattern) => pattern.test(message));
  return isChunkLoad ? (
    <ChunkLoadFallback />
  ) : (
    <RuntimeErrorFallback onReset={resetErrorBoundary} />
  );
}

const RefreshErrorBoundary = ({ children }: { children: ReactNode }) => {
  const location = useLocation();
  return (
    <ErrorBoundary
      FallbackComponent={RefreshErrorBoundaryFallback}
      resetKeys={[location.pathname]}
    >
      {children}
    </ErrorBoundary>
  );
};

export default RefreshErrorBoundary;
